import {Button, Flex, Typography} from "antd";
import {CambioVisusModal, CambioVisusModalType} from "../../../../CambioVisus/CambioVisusModal/CambioVisusModal";
import React, {useEffect, useState} from "react";
import {AttachmentType, PracticeFileRow, UploadAttachment} from "../../../../../models/Practice/PracticeFIle";
import {PracticeProgressStatus} from "../../../../../models/Practice/Practice";
import {InsuranceProviderEclaim, InsuranceProviderIntegrationCodes} from "../../../../../models/InsuranceProvider";
import {InsuranceProviderAuthorizationHttpService} from "../../../../../services/Http/InsuranceProviderAuthorizationHttpService";
import {InsuranceProviderAuthorizationOperationType} from "../../../../../models/InsuranceProviderAuthorization";
import {
    PracticeDetail,
    PracticeProgress,
    ProceedNextRequest,
    UploadEstimateBodyRequest
} from "../../../../../models/Practice/PracticeDetail";
import {UploadFilesModal} from "../../../../Common/UploadFileModal/UploadFilesModal";
import {useModal} from "../../../../../utils/modals";
import {AuthService} from "../../../../../services/AuthService";
import {PracticesHttpService} from "../../../../../services/Http/PracticesHttpService";
import {NotificationService} from "../../../../../services/NotificationService";
import {useParams} from "react-router-dom";
import {PreventiveDialogTrigger} from "../Preventive/PreventiveDialogTrigger";
import {isNil} from "lodash";
import {claimClosed, useClaimStatus} from "../../../ClaimStatusProvider";
import {Utils} from "../../../../../services/Utils";
import {Employee} from "../../../../../models/Auth/Employee";

export interface UploadFilesWarrantyActivationProps{
    practiceDetail: PracticeDetail;
    insuranceProvider:InsuranceProviderEclaim,
    convention:string,
    onClickConfirmDocs:()=>void;
    files: Array<PracticeFileRow>;
    loadFiles:()=>void;
    progress: PracticeProgress[] | undefined,
    reloadDetail: (showNoteWarning?: boolean) => Promise<void>
    handleMiddleColumnSpin:(value:boolean)=>void;
}

export function UploadFilesWarrantyActivation(props:UploadFilesWarrantyActivationProps){

    const { Text} = Typography;
    const [dataToSend, setDataToSend] = useState<UploadAttachment>();
    const [insuranceAuthorizations, setInsuranceAuthorizations] = useState<string[]>([]);
    const { id } = useParams();

    const status = props.practiceDetail.status;
    const currentInsuranceProvider = props.practiceDetail.insuranceProvider;
    const isAxaProvider = currentInsuranceProvider.integrationCode === InsuranceProviderIntegrationCodes.AXA.toString();

    const {
        isOpen: isUploadVoucherModalOpen,
        open: openUploadVoucherModal,
        close: closeUploadVoucherModal
    } = useModal()

    const estimatedLoaded = props.files.find(file => file.type == AttachmentType.ESTIMATE) != undefined;
    const cambioVisusLoaded = props.files.find(file => file.type == AttachmentType.CAMBIO_VISUS) != undefined;
    const voucherLoaded = props.files.find(file => file.type == AttachmentType.VOUCHER) != undefined;

    const claimStatus = useClaimStatus();

    const uploadVoucher = () => {
        if (voucherLoaded) return;
        const dataToSend = {
            filename: "codice_alfanumerico.png",
            attachmentType: AttachmentType.VOUCHER
        }
        setDataToSend(dataToSend);
        openUploadVoucherModal();
    }

    const onClose = () => {
        closeUploadVoucherModal()
        props.loadFiles();
    }

    useEffect(() => {
        if (props.insuranceProvider.id) {
            InsuranceProviderAuthorizationHttpService.getByInsuranceProviderId(props.insuranceProvider.id)
                .then(res => setInsuranceAuthorizations(res.data))
                .catch(() => setInsuranceAuthorizations([]));
        }
    }, [props.insuranceProvider.id]);

    const cambioVisusRequired = !insuranceAuthorizations.includes(InsuranceProviderAuthorizationOperationType.CAMBIO_VISUS_NOT_REQUIRED);
    const voucherRequired = insuranceAuthorizations.includes(InsuranceProviderAuthorizationOperationType.VOUCHER_REQUIRED);
    const newVisusButtonEnabledInCambioVisusModal = !insuranceAuthorizations.includes(InsuranceProviderAuthorizationOperationType.NEW_VISUS_BUTTON_DISABLED);
    const cambioVisusModalType = insuranceAuthorizations.includes(InsuranceProviderAuthorizationOperationType.CAMBIO_VISUS_MODAL_RICETTA_OCULISTICA)
        ? CambioVisusModalType.RICETTA_OCULISTICA
        : CambioVisusModalType.CAMBIO_VISUS;

    const shouldConfirm = [
        PracticeProgressStatus.DA_CONTATTARE,
        PracticeProgressStatus.CONTATTATO,
        PracticeProgressStatus.PRESO_APPUNTAMENTO,
        PracticeProgressStatus.ERRORE_IN_DOCUMENTAZIONE
    ].includes(status) || (isAxaProvider && [PracticeProgressStatus.PIC_AUTORIZZATA].includes(status));

    const shouldAskAssistanceGenerali = insuranceAuthorizations.includes(InsuranceProviderAuthorizationOperationType.ASK_ASSISTANCE_ON_DOCUMENTATION) && [
        PracticeProgressStatus.DOCUMENTAZIONE_DA_INVIARE, PracticeProgressStatus.DOCUMENTAZIONE_INVIATA
    ].includes(status);

    const canConfirm = (
        estimatedLoaded &&
        (!cambioVisusRequired || cambioVisusLoaded) &&
        (!voucherRequired || voucherLoaded)
    )

    const canConfirmBonificoIntesa = (
        currentInsuranceProvider.integrationCode === InsuranceProviderIntegrationCodes.ISP.toString()
            && status === PracticeProgressStatus.CONTATTATO
            && props.practiceDetail.picMetadata.orderData?.fieldData?.pagamentoMetodo === "Bonifico"
    )

    const onDocConfimed = async () => {
        let operator = AuthService.getOperatorOnline();
        const body: ProceedNextRequest = { metadata: {operator: operator!.name }, event: PracticeProgressStatus.DOCUMENTAZIONE_DA_INVIARE}
        try {
            props.handleMiddleColumnSpin(true);
            const showNoteWarning = insuranceAuthorizations.includes(InsuranceProviderAuthorizationOperationType.SHOW_NOTE_WARNING_ON_DOC_CONFIRM) && [PracticeProgressStatus.PRESO_APPUNTAMENTO].includes(status);
            await PracticesHttpService.saveProceedNext(id ?? "", body);
            await props.reloadDetail(showNoteWarning);
        } catch (_) {
            NotificationService.getInstance().openNotificationError("Operazione non riuscita")
        } finally {
            props.handleMiddleColumnSpin(false);
        }
    }

    const onCheckAssistanceGenerali = async () => {
        if (isNil(id)) return;
        try {
            if (props.practiceDetail.picStatus.includes('AUT') && Utils.getLastProgressData(props.progress)?.name !== 'PIC_AUTORIZZATA') {
                console.warn("Pratica Generali in Situazione incosistente - stato " + props.practiceDetail.picStatus + " e pratica in pic non autorizzata - id " + id);
                await PracticesHttpService.rerunGeneraliProcedure(id);
                console.log("Rerun pratica Generali effettuato - id " + id);
            } else {
                console.log("Generali - stato pratica " + id + " CONFORME");
            }
            await props.reloadDetail(true);
        } catch (_) {
            NotificationService.getInstance().openNotificationError("Operazione non riuscita")
        }
    }

    const onConfermaBonificoIntesa = async () => {
        if (isNil(id)) return;
        try {
            let operator:Employee|null = AuthService.getOperatorOnline();
            await PracticesHttpService.saveForceProceedNext(id, {
                metadata: { operator:operator?.name ?? "" },
                event: PracticeProgressStatus.PIC_AUTORIZZATA
            })
            await props.reloadDetail(true);
        } catch (_) {
            NotificationService.getInstance().openNotificationError("Operazione non riuscita")
        }
    }

    const onPreventiveSubmit = async (body: UploadEstimateBodyRequest) => {
        if (isNil(id)) return;
        try {
            const response = await PracticesHttpService.uploadEstimate(id, body);
            const compressed = response.data ? response.data.compressed : false;
            const message = compressed ? "Preventivo compresso correttamente e salvato" : "Preventivo salvato";
            NotificationService.getInstance().openNotificationSuccess(message);
        } catch (error:any) {
            console.log(error);
            let message = "Impossibile salvare il preventivo";
            if (error.response.status === 400) {
                const data = error.response.data;
                message = data.message;
            }
            NotificationService.getInstance().openNotificationError(message);
        } finally {
            props.loadFiles();
        }
    }

    const disablePreventiveButtons = (claimStatus: string, useIpClause:boolean, documentLoaded:boolean)=> {
        // disabilita il pulsante se la pratica è chiusa oppure usa clusola aonAxa (se prevjsta) oppure (non è caricato il file relativo e la pratica è in stato PIC non autorizzata)
        const providersToCheck = [InsuranceProviderIntegrationCodes.ISP.valueOf(), InsuranceProviderIntegrationCodes.AON.valueOf()];
        const disableButtonForIp = providersToCheck.includes(props.insuranceProvider.code);
        const disableForAxa = isAxaProvider && [PracticeProgressStatus.PIC_AUTORIZZATA].includes(status) && estimatedLoaded;
        return claimClosed(claimStatus) || (useIpClause && (disableButtonForIp || disableForAxa)) || (!documentLoaded && status === PracticeProgressStatus.PIC_NON_AUTORIZZATA);
    }

    const disableConfirmButtons = (claimStatus: string)=> {
        const disableForAxa = isAxaProvider && Utils.getLastProgressData(props.progress)?.name === 'DOCUMENTAZIONE_DA_INVIARE';
        return claimClosed(claimStatus) || !canConfirm || disableForAxa;
    }

    const disableButtons = (claimStatus: string, useIpClause:boolean, documentLoaded:boolean)=> {
        // disabilita il pulsante se la pratica è chiusa oppure usa clusola aon o ISP (se prevjsta) oppure (non è caricato il file relativo e la pratica è in stato PIC non autorizzata)
        const providersToCheck = [InsuranceProviderIntegrationCodes.ISP.valueOf(), InsuranceProviderIntegrationCodes.AON.valueOf()];
        const disableButtonForIp = providersToCheck.includes(props.insuranceProvider.code);
        const disableButtonForAonAxa = props.insuranceProvider.integrationCode === InsuranceProviderIntegrationCodes.AON.toString() || isAxaProvider;
        return claimClosed(claimStatus) || (useIpClause && (disableButtonForIp || disableButtonForAonAxa)) || (!documentLoaded && status === PracticeProgressStatus.PIC_NON_AUTORIZZATA);
    }

    return <>
    { currentInsuranceProvider.integrationCode === InsuranceProviderIntegrationCodes.AXA.toString() ?
        <Text strong>Documenti obbligatori il calcolo totali</Text> : <Text strong>Documenti obbligatori per attivazione garanzia</Text>}
        <Flex gap={"small"} className={"margin-y-10px"}>
            <PreventiveDialogTrigger disabled={disablePreventiveButtons(claimStatus, true, estimatedLoaded)}
                                     isLoaded={estimatedLoaded}
                                     onSubmit={onPreventiveSubmit}
                                     agreement={props.practiceDetail.agreement}
                                     practiceDetail={props.practiceDetail}
                                     integrationCode={currentInsuranceProvider.integrationCode}
                                     insuranceProviderId={currentInsuranceProvider.id}
                                     locationCode={props.practiceDetail.shopName}
            />

            { cambioVisusRequired && <CambioVisusModal disabled={disableButtons(claimStatus, true, cambioVisusLoaded)}
                                                       shopGroup={props.practiceDetail.shopGroup} isLoaded={cambioVisusLoaded}
                                                       loadFiles={props.loadFiles} patient={props.practiceDetail.patient}
                                                       practiceDetail={props.practiceDetail} formType={cambioVisusModalType}
                                                       newVisusButtonEnabled={newVisusButtonEnabledInCambioVisusModal} ></CambioVisusModal>}
            { voucherRequired && <Button disabled={disableButtons(claimStatus, false, voucherLoaded)} type={"primary"} className={voucherLoaded ? "luxottica-button-success" : ""} onClick={uploadVoucher}>Codice alfanumerico</Button>}
            { dataToSend && <UploadFilesModal attachmentData={dataToSend} isModalOpen={isUploadVoucherModalOpen} practiceDetail={props.practiceDetail} onClose={onClose}></UploadFilesModal> }
            { shouldConfirm && <Button disabled={disableConfirmButtons(claimStatus)} type={"primary"} onClick={onDocConfimed} >Conferma doc.</Button> }
            { shouldAskAssistanceGenerali && <Button disabled={claimClosed(claimStatus)} type={"primary"} onClick={onCheckAssistanceGenerali}>Verifica stato PIC</Button> }
            { ((AuthService.isAdmin() || AuthService.isAdminLux()) && canConfirmBonificoIntesa) && <Button type={"primary"} onClick={onConfermaBonificoIntesa}>Conferma bonifico Intesa</Button>}
        </Flex></>
}